/**
 * `<caos-coverage-list>` — per-artifact code coverage, ordered by what needs attention.
 * (Catalog: Status & feedback › CoverageList — per-artifact code coverage, ordered worst-first and marked against a required floor.)
 *
 * Coverage is the one number on the Unit Tests screen that gates a deploy, so this list
 * is read for a decision rather than for information: which artifacts are below the bar,
 * and by how much. Everything about it follows from that.
 *
 * It orders WORST FIRST by default, not alphabetically. A list sorted by name buries the
 * three failing artifacts somewhere in the middle of two hundred passing ones, and the
 * reader's actual question — what is blocking me — takes a scan to answer. Alphabetical
 * ordering is available (`order="name"`) for the case where somebody is looking up a
 * specific artifact they can already name.
 *
 * `required` is drawn as a real threshold rather than a colour: each row states its
 * percentage against the floor, and a row below it is marked in text as well as in hue.
 * The summary line states how many are short, because "12 of 240 below 75%" is the whole
 * screen for somebody deciding whether they can deploy.
 *
 * Coverage of an artifact with no executable lines is NOT zero, and this is the case that
 * makes naive coverage lists lie: an interface, a constants file or a type-only module has
 * nothing to cover, and scoring it 0% puts it at the top of a worst-first list forever
 * while nothing is wrong. Such rows report "no executable lines" and are excluded from
 * both the ordering and the shortfall count. Styling seams: `::part(list)`,
 * `::part(summary)`, `::part(row)`, `::part(name)`, `::part(bar)`, `::part(percent)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './usage-bar.js';

/** One artifact's coverage. `covered`/`total` are LINE counts, not a pre-computed percentage. */
export interface CaosCoverageEntry {
  readonly name: string;
  readonly covered: number;
  readonly total: number;
}

export const COVERAGE_REQUIRED_DEFAULT = 75;

const sheet = makeSheet(`
  :host { display: block; }

  .list { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .summary {
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
  .summary[data-short="yes"] strong { color: var(--caos-color-danger); }

  .row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 96px 72px;
    align-items: center;
    gap: var(--caos-space-3);
  }

  .name {
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .percent {
    font-family: var(--caos-font);
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    text-align: right;
    color: var(--caos-color-text-muted);
  }
  .row[data-below="yes"] .percent { color: var(--caos-color-danger); font-weight: 600; }
  .row[data-uncoverable="yes"] .percent { font-style: italic; }

  .empty {
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
`);

/**
 * The coverage percentage of an entry, or `undefined` when there is nothing to cover.
 *
 * `undefined` rather than 0 is the whole point: an artifact with no executable lines is not
 * badly covered, it is uncoverable, and scoring it zero pins it to the top of a worst-first
 * list forever while nothing is wrong.
 */
export function coveragePercent(entry: CaosCoverageEntry): number | undefined {
  if (!Number.isFinite(entry.total) || entry.total <= 0) return undefined;
  const covered = Number.isFinite(entry.covered) ? Math.max(0, entry.covered) : 0;
  return Math.max(0, Math.min(100, (covered / entry.total) * 100));
}

/**
 * Worst first, with uncoverable artifacts at the END rather than the top: they have no percentage,
 * and they are not the thing anybody scanning this list is looking for. Ties break by name so the
 * order is stable rather than whatever the input happened to be.
 */
function worstFirst(a: CaosCoverageEntry, b: CaosCoverageEntry): number {
  const pa = coveragePercent(a);
  const pb = coveragePercent(b);
  if (pa === undefined && pb === undefined) return a.name.localeCompare(b.name);
  if (pa === undefined) return 1;
  if (pb === undefined) return -1;
  if (pa !== pb) return pa - pb;
  return a.name.localeCompare(b.name);
}

export class CaosCoverageList extends CaosElement {
  static get observedAttributes(): string[] {
    return ['required', 'order', 'empty-text'];
  }

  private listEl: HTMLDivElement;
  private summaryEl: HTMLParagraphElement;
  private rowsEl: HTMLDivElement;
  private data: readonly CaosCoverageEntry[] = [];

  constructor() {
    super(sheet);

    this.listEl = document.createElement('div');
    this.listEl.className = 'list';
    this.listEl.setAttribute('part', 'list');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.rowsEl = document.createElement('div');
    this.rowsEl.className = 'list';

    this.listEl.append(this.summaryEl, this.rowsEl);
    this.root.append(this.listEl);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get entries(): readonly CaosCoverageEntry[] {
    return this.data;
  }

  set entries(next: readonly CaosCoverageEntry[]) {
    this.data = Array.isArray(next) ? next : [];
    this.render();
  }

  get required(): number {
    const n = Number(this.getAttribute('required'));
    return this.hasAttribute('required') && Number.isFinite(n) ? n : COVERAGE_REQUIRED_DEFAULT;
  }

  /**
   * The artifacts below the required floor, worst first. Uncoverable artifacts are absent by
   * construction — they have no percentage to be below anything.
   *
   * Worst first regardless of the `order` attribute, because this is a work list rather than a
   * view: a caller reading it is deciding what to fix, and arrival order would hand them the
   * shortfall in whatever sequence the report happened to serialise.
   */
  get below(): readonly CaosCoverageEntry[] {
    return this.data
      .filter((e) => {
        const percent = coveragePercent(e);
        return percent !== undefined && percent < this.required;
      })
      .sort(worstFirst);
  }

  /** The rows in the order they will be drawn. */
  private ordered(): readonly CaosCoverageEntry[] {
    const rows = [...this.data];
    if (this.getAttribute('order') === 'name') {
      return rows.sort((a, b) => a.name.localeCompare(b.name));
    }
    return rows.sort(worstFirst);
  }

  private render(): void {
    if (this.data.length === 0) {
      this.summaryEl.textContent = '';
      this.summaryEl.removeAttribute('data-short');
      const empty = document.createElement('p');
      empty.className = 'empty';
      empty.textContent = this.getAttribute('empty-text') ?? 'No coverage reported.';
      this.rowsEl.replaceChildren(empty);
      return;
    }

    const short = this.below.length;
    const measured = this.data.filter((e) => coveragePercent(e) !== undefined).length;
    this.summaryEl.replaceChildren(...this.summaryNodes(short, measured));
    this.summaryEl.setAttribute('data-short', short > 0 ? 'yes' : 'no');

    this.rowsEl.replaceChildren(...this.ordered().map((entry) => this.rowFor(entry)));
  }

  private summaryNodes(short: number, measured: number): Node[] {
    const strong = document.createElement('strong');
    if (short === 0) {
      strong.textContent = `all ${measured} at or above ${this.required}%`;
      return [strong];
    }
    strong.textContent = `${short} below ${this.required}%`;
    return [strong, document.createTextNode(` of ${measured} measured`)];
  }

  private rowFor(entry: CaosCoverageEntry): HTMLElement {
    const percent = coveragePercent(entry);
    const row = document.createElement('div');
    row.className = 'row';
    row.setAttribute('part', 'row');
    row.setAttribute('data-below', percent !== undefined && percent < this.required ? 'yes' : 'no');
    row.setAttribute('data-uncoverable', percent === undefined ? 'yes' : 'no');

    const name = document.createElement('span');
    name.className = 'name';
    name.setAttribute('part', 'name');
    name.textContent = entry.name;
    name.title = entry.name;

    const bar = document.createElement('caos-usage-bar');
    bar.setAttribute('part', 'bar');
    bar.setAttribute('hide-value', '');
    if (percent === undefined) {
      bar.setAttribute('used', '0');
    } else {
      bar.setAttribute('used', String(entry.covered));
      bar.setAttribute('maximum', String(entry.total));
      // Coverage runs the other way from consumption: LOW is the problem. The usage bar's own
      // thresholds would redden a well-covered artifact, so the intent is stated explicitly here
      // rather than left to a ramp pointing the wrong way.
      bar.setAttribute('intent', percent < this.required ? 'danger' : 'success');
    }

    const pct = document.createElement('span');
    pct.className = 'percent';
    pct.setAttribute('part', 'percent');
    pct.textContent = percent === undefined ? 'no lines' : `${Math.round(percent)}%`;

    row.append(name, bar, pct);
    row.setAttribute(
      'aria-label',
      percent === undefined
        ? `${entry.name}: no executable lines`
        : `${entry.name}: ${Math.round(percent)} percent, ${entry.covered} of ${entry.total} lines${
            percent < this.required ? `, below the required ${this.required} percent` : ''
          }`,
    );
    return row;
  }
}

define('caos-coverage-list', CaosCoverageList);

declare global {
  interface HTMLElementTagNameMap {
    'caos-coverage-list': CaosCoverageList;
  }
}
